import { baseApi } from "../../../../core/data/rtk/baseQuery";
import { ApiResponseType } from "../../../../core/data/rtk/apiResponseType";
import { StudentResponseType, Data } from "../types/studentResponse";
import { StudentPayloadType } from "../types/studentRequest";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";

export const studentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllStudents: builder.query<
      ApiResponseType<Data>,
      {
        size?: number;
        search?: string;
        page?: number;
        center_id?: number;
        course_id?: number;
        stage_id?: number;
        session_id?: number;
        batch_id?: number;
        status?: string;
        fee_type?: string;
        gender?: string;
        sort_by?: string;
        sort_order?: string;
        marks?: boolean;
      }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.size) queryParams.append("size", params.size.toString());
        if (params?.search) queryParams.append("search", params.search);
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.center_id)
          queryParams.append("center_id", params.center_id.toString());
        if (params?.course_id)
          queryParams.append("course_id", params.course_id.toString());
        if (params?.stage_id)
          queryParams.append("stage_id", params.stage_id.toString());
        if (params?.session_id)
          queryParams.append("session_id", params.session_id.toString());
        if (params?.batch_id)
          queryParams.append("batch_id", params.batch_id.toString());
        if (params?.status) queryParams.append("status", params.status);
        if (params?.fee_type) queryParams.append("fee_type", params.fee_type);
        if (params?.gender) queryParams.append("gender", params.gender);
        if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
        if (params?.sort_order)
          queryParams.append("sort_order", params.sort_order);
        if (params?.marks) queryParams.append("marks", "true");

        return `students/get-all?${queryParams.toString()}`;
      },
      providesTags: ["Students"],
    }),
    addStudent: builder.mutation<
      ApiResponseType<{ student: StudentResponseType }>,
      StudentPayloadType & { photo?: File }
    >({
      query: (payload) => {
        const { photo, ...studentData } = payload;
        return {
          url: "students/save",
          method: "POST",
          body: studentData,
        };
      },
      async onQueryStarted({ photo }, { queryFulfilled, dispatch }) {
        try {
          // Wait for the student to be created
          const {
            data: {
              data: { student },
            },
          } = await queryFulfilled;

          // If there's a photo, upload it
          if (photo) {
            await dispatch(
              studentApi.endpoints.uploadStudentPhoto.initiate({
                id: student.id.toString(),
                file: photo,
              })
            ).unwrap();
          }
        } catch (error) {
          console.error("Error in student creation flow:", getApiErrorMessage(error));
          throw error;
        }
      },
      invalidatesTags: ["Students"],
    }),
    getStudentById: builder.query<
      ApiResponseType<{ student: StudentResponseType }>,
      { id: string }
    >({
      query: ({ id }) => `students/get/${id}`,
      providesTags: ["Students"],
    }),
    getStudentByIdPublic: builder.query<
      ApiResponseType<{ student: StudentResponseType }>,
      { id: string }
    >({
      query: ({ id }) => `public/students/get/${id}`,
      providesTags: ["Students"],
    }),
    updateStudentPublic: builder.mutation<
      ApiResponseType<{ student: StudentResponseType }>,
      { id: string; payload: Partial<StudentPayloadType> }
    >({
      query: ({ id, payload }) => ({
        url: `public/students/update/${id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Students"],
    }),
    updateStudent: builder.mutation<
      ApiResponseType<{ student: StudentResponseType }>,
      { id: string; payload: Partial<StudentPayloadType> }
    >({
      query: ({ id, payload }) => ({
        url: `students/update/${id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Students"],
    }),
    updateStudentStatus: builder.mutation<
      ApiResponseType<{ student: StudentResponseType }>,
      { id: string; status: string }
    >({
      query: ({ id, status }) => ({
        url: `students/update-status/${id}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["Students"],
    }),
    deleteStudent: builder.mutation<
      ApiResponseType<{ deleted: true }>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `students/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Students", "Users"],
    }),
    uploadStudentPhoto: builder.mutation<
      ApiResponseType<{ data: string }>,
      { id: string; file: File }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("image", file);

        return {
          url: `students/upload-photo/${id}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Students"],
    }),
    uploadStudentDobDocument: builder.mutation<
      ApiResponseType<{ data: string }>,
      { id: string; file: File }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `students/upload-dob/${id}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Students"],
    }),
    uploadStudentClassProofDocument: builder.mutation<
      ApiResponseType<{ data: string }>,
      { id: string; file: File }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `students/upload-class-proof/${id}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Students"],
    }),
    markStudentEnrolled: builder.mutation<
      ApiResponseType<{ student: StudentResponseType }>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `students/mark-enrolled/${id}`,
        method: "POST",
      }),
      invalidatesTags: ["Students"],
    }),
    getStudentsEligibleForBooks: builder.query<
      ApiResponseType<Data>,
      {
        size?: number;
        page?: number;
        session_id?: number;
        course_id?: number;
        stage_id?: number;
      }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.size) queryParams.append("size", params.size.toString());
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.session_id)
          queryParams.append("session_id", params.session_id.toString());
        if (params?.course_id)
          queryParams.append("course_id", params.course_id.toString());
        if (params?.stage_id)
          queryParams.append("stage_id", params.stage_id.toString());

        return `students/get-students-eligible-for-books?${queryParams.toString()}`;
      },
      providesTags: ["Students"],
    }),
    transferStudent: builder.mutation<
      ApiResponseType<any>,
      { student_id: number; to_center_id: number; remarks?: string }
    >({
      query: (payload) => ({
        url: "student-transfers/transfer",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Students"],
    }),

    // Quick Register a student with minimal info
    quickSaveStudent: builder.mutation<
      ApiResponseType<{ student: StudentResponseType }>,
      {
        name: string;
        fathers_name: string;
        gender: string;
        course_id: number;
        stage_id: number;
        session_id: number;
        phone: string;
        class: string;
        center_id: number;
        batch_id: number | null;
        photo?: File;
      }
    >({
      query: ({ photo: _photo, ...payload }) => ({
        url: "students/quick-save",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted({ photo }, { queryFulfilled, dispatch }) {
        try {
          const { data: { data: { student } } } = await queryFulfilled;
          if (photo) {
            await dispatch(
              studentApi.endpoints.uploadStudentPhoto.initiate({
                id: student.id.toString(),
                file: photo,
              })
            ).unwrap();
          }
        } catch (error) {
          console.error("Error in quick save flow:", getApiErrorMessage(error));
        }
      },
      invalidatesTags: ["Students"],
    }),
  }),
});

export const {
  useGetAllStudentsQuery,
  useAddStudentMutation,
  useGetStudentByIdQuery,
  useGetStudentByIdPublicQuery,
  useUpdateStudentMutation,
  useUpdateStudentPublicMutation,
  useUpdateStudentStatusMutation,
  useDeleteStudentMutation,
  useUploadStudentPhotoMutation,
  useUploadStudentDobDocumentMutation,
  useUploadStudentClassProofDocumentMutation,
  useMarkStudentEnrolledMutation,
  useGetStudentsEligibleForBooksQuery,
  useTransferStudentMutation,
  useQuickSaveStudentMutation,
} = studentApi;
